import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, setLogLevel, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { getMessaging, getToken } from 'firebase/messaging';

// Your Firebase configuration, obtained from the Firebase console.
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

const auth = getAuth(app);
const db = getFirestore(app); // Volver a la inicialización estándar
const functions = getFunctions(app, 'us-central1');
const messaging = getMessaging(app);

// Función para validar VAPID key
const validateVapidKey = (key) => {
    try {
        // Un VAPID key válido debe ser decodificable (soporta standard y URL-safe Base64)
        atob(key.replace(/-/g, '+').replace(/_/g, '/'));
        return true;
    } catch (error) {
        console.error('VAPID key inválido:', error.message);
        return false;
    }
};

// VAPID key - IMPORTANTE: Debe ser un key válido de Firebase Console
// Generado desde Firebase Console > Project Settings > Cloud Messaging > Web Push certificates
let VAPID_KEY = 'BBwwWPNWAbBsue8HnhwxwXWxBnRFqN-GxRSWeeeoLvTckXN5LwLpWSs18UysOVkAJSJLwD3xeXzpG1JTCZeVSuk';

// Validar VAPID key al inicializar
if (!validateVapidKey(VAPID_KEY)) {
    console.error('❌ VAPID key inválido. Las notificaciones no funcionarán.');
    console.error('Para solucionarlo:');
    console.error('1. Ve a https://console.firebase.google.com/');
    console.error('2. Selecciona tu proyecto: plataforma-invest-f1675');
    console.error('3. Ve a Project Settings > Cloud Messaging');
    console.error('4. En "Web Push certificates", haz clic en "Generate key pair"');
    console.error('5. Copia el VAPID key generado (debe ser una cadena base64)');
    console.error('6. Reemplaza el VAPID_KEY en src/firebase.js');
    console.error('7. Reinicia la aplicación');
} else {
    console.log('✅ VAPID key válido - notificaciones listas');
}

// Emulator connection
// --- Modo "real" en local: por defecto, correr en localhost conecta a los emuladores (seguro,
// nunca toca datos reales). Para probar con datos REALES desde local (ej. npm start con
// REACT_APP_USE_REAL_FIREBASE=true), se salta la conexión a emuladores y usa el proyecto real de
// Firebase configurado en REACT_APP_FIREBASE_*. OJO: en ese modo, cualquier acción que hagas
// (depósitos, retiros, acciones de admin) es REAL, no una simulación. ---
const USE_REAL_FIREBASE = process.env.REACT_APP_USE_REAL_FIREBASE === 'true';

if (window.location.hostname === 'localhost' && !USE_REAL_FIREBASE) {
    try {
        connectAuthEmulator(auth, "http://localhost:9099");
        connectFirestoreEmulator(db, 'localhost', 8081);
        connectFunctionsEmulator(functions, 'localhost', 5001);
        setLogLevel('debug');

        // Adjuntar a window para depuración en consola
        window.firebaseForDebug = {
            auth,
            db,
            functions,
            httpsCallable
        };

    } catch (error) {
        console.error("Error connecting to Firebase emulators:", error);
    }
} else if (window.location.hostname === 'localhost' && USE_REAL_FIREBASE) {
    // eslint-disable-next-line no-console
    console.warn(
        '⚠️ MODO REAL ACTIVADO: esta sesión local está conectada al proyecto REAL de Firebase (' +
        firebaseConfig.projectId + '), no al emulador. Las acciones que hagas (depósitos, retiros, ' +
        'cambios de perfil, etc.) son reales y afectan datos de producción.'
    );
} else {
    // console.log("Production mode: Connecting to live Firebase services.");
}

const createGroupedCallable = (functionName) => {
    const callable = httpsCallable(functions, 'api');
    return (data) => {
        const requestData = { ...data, __fn: functionName };
        return callable(requestData);
    };
};

// --- NUEVA FUNCIÓN PARA GESTIONAR PERMISOS Y TOKENS DE NOTIFICACIONES ---
export const requestNotificationPermission = async (userId) => {
    // Verificar si el navegador soporta notificaciones
    if (!('Notification' in window)) {
        // ...
        return;
    }

    // Verificar si el service worker está registrado ANTES de intentar obtener el token
    if (!('serviceWorker' in navigator)) {
        // ...
        return;
    }

    try {
        // ...
        let registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');

        if (!registration) {
            console.log("Service Worker no registrado. Intentando registrar...");
            try {
                registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
                    scope: '/'
                });
                // ...

                // Esperar a que el Service Worker esté activo
                await navigator.serviceWorker.ready;
                // ...
            } catch (regError) {
                console.error("❌ Error al registrar Service Worker:", regError);
                return;
            }
        } else {
            // ...
        }
    } catch (swError) {
        console.error("❌ Error al verificar Service Worker:", swError);
        return;
    }

    if (Notification.permission === 'granted') {
        // ...
        try {
            // Verificar que messaging esté disponible
            if (!messaging) {
                console.error("Firebase Messaging no está inicializado.");
                return;
            }

            const currentToken = await getToken(messaging, {
                vapidKey: VAPID_KEY
            });

            if (currentToken) {
                // ...

                // Limpiar tokens antiguos antes de agregar el nuevo
                const userProfileRef = doc(db, `users/${userId}/profile/data`);
                try {
                    await updateDoc(userProfileRef, {
                        fcmTokens: [currentToken] // Reemplazar con array que contiene solo el token actual
                    });
                    // ...
                } catch (firestoreError) {
                    console.error("Error al guardar token en Firestore:", firestoreError);
                }
            } else {
                // ...
            }
        } catch (tokenError) {
            console.error('Error específico al obtener el token FCM:', tokenError);

            // Manejo específico del error atob
            if (tokenError.message && tokenError.message.includes('atob')) {
                console.error('Error de decodificación base64. Posibles causas:');
                console.error('1. VAPID key malformado o inválido');
                console.error('2. Token FCM corrupto almacenado previamente');
                console.error('3. Problema con la configuración de Firebase Messaging');

                // Intentar limpiar cualquier token corrupto
                try {
                    const userProfileRef = doc(db, `users/${userId}/profile/data`);
                    await updateDoc(userProfileRef, {
                        fcmTokens: [] // Limpiar tokens corruptos
                    });
                    console.log("Tokens FCM corruptos limpiados.");
                } catch (cleanupError) {
                    console.error("Error al limpiar tokens corruptos:", cleanupError);
                }
            } else if (tokenError.code === 'messaging/permission-blocked') {
                // ...
            } else if (tokenError.code === 'messaging/unsupported-browser') {
                // ...
            } else {
                console.error('Error desconocido al obtener token:', tokenError.code, tokenError.message);
            }
        }
    } else if (Notification.permission !== 'denied') {
        // ...
        try {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                // ...
                await requestNotificationPermission(userId); // Llamada recursiva para obtener el token
            } else {
                // ...
            }
        } catch (permissionError) {
            console.error("Error al solicitar permiso de notificaciones:", permissionError);
        }
    } else {
        // ...
    }
};

// Función de diagnóstico para Firebase Messaging
export const diagnoseMessaging = async () => {
    // ...
    // ...

    // Verificar soporte del navegador
    if (!('Notification' in window)) {
        console.error('❌ Notificaciones no soportadas en este navegador');
        return false;
    }

    if (!('serviceWorker' in navigator)) {
        console.error('❌ Service Worker no soportado en este navegador');
        return false;
    }

    // Verificar permisos
    // ...

    // Verificar service worker
    try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
            // ...
        } else {
            // ...
        }
    } catch (error) {
        console.error('❌ Error al verificar Service Worker:', error);
    }

    // Verificar VAPID key
    if (validateVapidKey(VAPID_KEY)) {
        // ...
    } else {
        console.error('❌ VAPID key inválido - necesitas generar uno nuevo en Firebase Console');
    }

    // Verificar Firebase Messaging
    if (messaging) {
        // ...
    } else {
        console.error('❌ Firebase Messaging no inicializado');
    }

    return true;
};

// Función de diagnóstico para el sistema de referidos
export const diagnoseReferrals = async () => {
    // ...

    try {
        // Obtener datos del equipo
        const teamData = await callableFunctions.getTeamData();
        // ...

        // Mostrar estadísticas
        // ...
        Object.keys(teamData.stats || {}).forEach(level => {
            const stat = teamData.stats[level];
            // ...
        });

        // Mostrar referidos por nivel
        // ...
        Object.keys(teamData.allUsersByLevel || {}).forEach(level => {
            const users = teamData.allUsersByLevel[level] || [];
            // ...
            users.slice(0, 3).forEach(user => {
                // ...
            });
            if (users.length > 3) {
                // ...
            }
        });

        // Verificar referrer
        if (teamData.referrerUsername) {
            // ...
        } else {
            // ...
        }

        return teamData;
    } catch (error) {
        console.error('❌ Error al diagnosticar referidos:', error);
        return null;
    }
};

// Hacer funciones de diagnóstico disponibles globalmente para debugging
if (typeof window !== 'undefined') {
    window.diagnoseFirebaseMessaging = diagnoseMessaging;
    window.diagnoseReferrals = diagnoseReferrals;
}

export const callableFunctions = {
    togglePlatformSetting: createGroupedCallable('togglePlatformSetting'),
    assignDailyGains: createGroupedCallable('assignDailyGains'),
    revertLastPayment: createGroupedCallable('revertLastPayment'),
    adminUpdateUserAuth: createGroupedCallable('adminUpdateUserAuth'),
    processWithdrawal: createGroupedCallable('processWithdrawal'),
    cancelAgreement: createGroupedCallable('cancelAgreement'),
    processSmallOwnerRequest: createGroupedCallable('processSmallOwnerRequest'),
    createSmallOwnerRequest: httpsCallable(functions, 'createSmallOwnerRequest'),
    cancelSmallOwnerRequest: createGroupedCallable('cancelSmallOwnerRequest'),
    checkUsernameAvailability: createGroupedCallable('checkUsernameAvailability'),
    createUserProfile: httpsCallable(functions, 'createUserProfile'),
    checkReferralCodeValidity: createGroupedCallable('checkReferralCodeValidity'),
    getTeamData: createGroupedCallable('getTeamData'),
    getNetworkChildren: createGroupedCallable('getNetworkChildren'),
    searchNetworkMembers: createGroupedCallable('searchNetworkMembers'),
    migrateExistingAgreements: createGroupedCallable('migrateExistingAgreements'),
    deleteUserAndData: createGroupedCallable('deleteUserAndData'),
    setUserRole: createGroupedCallable('setUserRole'),
    setAdminClaim: createGroupedCallable('setAdminClaim'),
    adminAdjustUserBalance: createGroupedCallable('adminAdjustUserBalance'),
    deactivateAccelerator: createGroupedCallable('deactivateAccelerator'),
    bootstrapAdmin: createGroupedCallable('bootstrapAdmin'),
    reprocessStuckAgreements: createGroupedCallable('reprocessStuckAgreements'),
    resyncAllBalances: createGroupedCallable('resyncAllBalances'),
    recalculateMissingCommissions: createGroupedCallable('recalculateMissingCommissions'),
    correctNegativeBalances: createGroupedCallable('correctNegativeBalances'),
    recalculateAllCommissions: createGroupedCallable('recalculateAllCommissions'),
    deleteAllCommissions: createGroupedCallable('deleteAllCommissions'),
    cleanAndRebuildAllCommissions: createGroupedCallable('cleanAndRebuildAllCommissions'),
    verifyAndPayMissingCommissions: createGroupedCallable('verifyAndPayMissingCommissions'),
    adminCreateManualAgreement: createGroupedCallable('adminCreateManualAgreement'),
    correctInvestmentTransactions: createGroupedCallable('correctInvestmentTransactions'),
    recalculateAllAgreementsProgress: createGroupedCallable('recalculateAllAgreementsProgress'),
};

export { auth, db, functions, messaging, app as default };